/**
 * HierarchicalDialogPlayer.js
 * 
 * Created on 17-sep-2013, 12:01:53
 *
 * @author Ivo Swartjes for Ranj
 **/
/**
 * @namespace ranj
 * @param {Object} ranj
 */

import EventDispatcher from "../../../../util/EventDispatcher";
import { isNull } from "../../../../util/TypeUtil";
import DefaultDialogPlayer from "./DefaultDialogPlayer";
import PlayMode from "./PlayMode";
import DefaultDialogPlayerEvent from "./DefaultDialogPlayerEvent";
import ContentTypes from "../../../content/ContentTypes";

export const HierarchicalDialogPlayerEvent = {};

HierarchicalDialogPlayerEvent.SUB_DIALOG_START = "HierarchicalDialogPlayerEvent.SUB_DIALOG_START";
HierarchicalDialogPlayerEvent.SUB_DIALOG_END = "HierarchicalDialogPlayerEvent.SUB_DIALOG_END";

export default class HierarchicalDialogPlayer extends EventDispatcher {
    constructor(dialogFactory, timeTickInterval, maxDepth) {
        super();

        if (isNull(timeTickInterval))
            timeTickInterval = 100;

        this._maxDepth = maxDepth;
        if (isNull(this._maxDepth)) {
            // Depth 3 = main dialog -> sub dialog -> sub-sub dialog
            this._maxDepth = 3;
        }

        this._dialogPlayers = [];
        this._dialogPlayerContentStack = [];

        for (let i = 0; i < this._maxDepth; i++) {
            const nwPlayer = new DefaultDialogPlayer(timeTickInterval);
            nwPlayer.setPlayMode(PlayMode.COMMANDS);
            this._dialogPlayers.push(nwPlayer);
        }

        this._addEventListeners();

        this._activePlayerIndex = 0;

        this._dialogFactory = dialogFactory;

        this._optionPlayerMap = new Map();

        this._onSubDialogEndBound = this._onSubDialogEnd.bind(this);

        this._eventHandlerBound = this._eventHandler.bind(this);
    }

    pauseTimers() {
        for (let i = 0; i < this._dialogPlayers.length; i++) {
            const player = this._dialogPlayers[i];
            player.pauseTimers();
        }
    }

    resumeTimers() {
        for (let i = 0; i < this._dialogPlayers.length; i++) {
            const player = this._dialogPlayers[i];
            player.resumeTimers();
        }
    }

    clearAutoTake() {
        for (let i = 0; i < this._dialogPlayers.length; i++) {
            const player = this._dialogPlayers[i];
            player.clearAutoTake();
        }
    }

    addEventListener(type, listener, scope) {
        super.addEventListener(type, listener, scope);

        this._dialogPlayers[0].addEventListener(type, this._eventHandlerBound, scope);
        for (let i = 1; i < this._dialogPlayers.length; i++) {
            // For the outside world, we're interested in START DIALOG & END DIALOG events only from the main dialog.
            if (type !== DefaultDialogPlayerEvent.END_DIALOG && type !== DefaultDialogPlayerEvent.START_DIALOG) {
                this._dialogPlayers[i].addEventListener(type, this._eventHandlerBound, scope);
            }
        }
    }

    removeEventListener(type, listener, scope) {
        super.removeEventListener(type, listener, scope);

        for (let i = 0; i < this._dialogPlayers.length; i++) {
            const player = this._dialogPlayers[i];
            player.removeEventListener(type, this._eventHandlerBound, scope);
        }
    }

    _eventHandler({ type, data }) {
        this.dispatchEvent(type, data);
    }

    setDialog(value, conditionSolver) {
        this._removeEventListeners();

        this._dialogPlayers[0].setDialog(value, conditionSolver);
        this._conditionSolver = conditionSolver;

        this._addEventListeners();
    }

    isPlayingSubDialog() {
        return this._activePlayerIndex > 0;
    }

    next() {
        this._dialogPlayers[this._activePlayerIndex].next();
    }

    takeOption(option) {
        const player = this._optionPlayerMap.get(option);
        player.takeOption(option);

        // Set the active player to the dialog player where the dialog option originated from.
        for (let i = 0; i < this._dialogPlayers.length; i++) {
            if (this._dialogPlayers[i] === player) {
                this._activePlayerIndex = i;
            }
        }

        this._optionPlayerMap.delete(option);
    }

    reset() {
        for (let i = 0; i < this._dialogPlayers.length; i++) {
            const player = this._dialogPlayers[i];
            player.reset();
        }
    }

    destroy() {
        for (let i = 0; i < this._dialogPlayers.length; i++) {
            const player = this._dialogPlayers[i];
            player.destroy();
        }
        this._dialogPlayers = null;
        this._dialogPlayerContentStack = null;
        this._dialogFactory = null;
        this._optionPlayerMap = null;
        this._onSubDialogEndBound = null;
        this._eventHandlerBound = null;
    }

    _addEventListeners() {
        for (let i = 0; i < this._dialogPlayers.length; i++) {
            const player = this._dialogPlayers[i];
            player.addEventListener(DefaultDialogPlayerEvent.DIALOG_OPTION, this._interceptDialogOption, this);
            player.addEventListener(DefaultDialogPlayerEvent.DIALOG_TIME_OUT, this._interceptDialogTimeOut, this);
            player.addEventListener(DefaultDialogPlayerEvent.CONTENT, this._interceptDialogContent, this);
        }
    }

    _removeEventListeners() {
        for (let i = 0; i < this._dialogPlayers.length; i++) {
            const player = this._dialogPlayers[i];
            player.removeEventListener(DefaultDialogPlayerEvent.DIALOG_OPTION, this._interceptDialogOption, this);
            player.removeEventListener(DefaultDialogPlayerEvent.DIALOG_TIME_OUT, this._interceptDialogTimeOut, this);
            player.removeEventListener(DefaultDialogPlayerEvent.CONTENT, this._interceptDialogContent, this);
        }
    }

    _interceptDialogOption({ data, target }) {
        // Remember, so that options are taken in the dialog player from which they originated.
        this._optionPlayerMap.set(data.option, target);
    }

    _interceptDialogTimeOut({ data, target }) {
        // Remember, so that options are taken in the dialog player from which they originated.
        this._optionPlayerMap.set(data, target);
    }

    _interceptDialogContent(event) {
        // Track "dialog content" -> start sub dialog.
        if (event.data && event.data.content !== null && event.data.content.getType() === ContentTypes.TYPE_DIALOG) {
            event.stopImmediatePropagation();
            const subDialogContent = event.data.content;
            const subDialog = this._dialogFactory.getDialog(subDialogContent.getDialogID());
            this._playSubDialog(subDialog, null, subDialogContent);
        }
    }

    _playSubDialog(dialog, conditionSolver, dialogContent) {
        if (isNull(conditionSolver)) {
            conditionSolver = this._conditionSolver;
        }

        // Switch to the sub player as the active player
        this._activePlayerIndex++;
        if (this._activePlayerIndex >= this._maxDepth) {
            // Cannot go this deep
            throw new Error(`Trying to start a subdialog at depth greater than the maximum depth of ${this._maxDepth}. Please initialize the hierarchical dialog player to be able to go deeper.`);
        }

        this._dialogPlayerContentStack[this._activePlayerIndex] = dialogContent;

        const subDialogPlayer = this._dialogPlayers[this._activePlayerIndex];
        subDialogPlayer.setDialog(dialog, conditionSolver);
        subDialogPlayer.addEventListener(DefaultDialogPlayerEvent.END_DIALOG, this._onSubDialogEndBound);

        this.dispatchEvent(HierarchicalDialogPlayerEvent.SUB_DIALOG_START, { subPlayer: subDialogPlayer, subPlayerDialogContent: dialogContent });

        subDialogPlayer.next();
    }

    _onSubDialogEnd(event) {
        this._dialogPlayers[this._activePlayerIndex].removeEventListener(DefaultDialogPlayerEvent.END_DIALOG, this._onSubDialogEndBound);
        this.dispatchEvent(HierarchicalDialogPlayerEvent.SUB_DIALOG_END, { subPlayer: this._dialogPlayers[this._activePlayerIndex], subPlayerDialogContent: this._dialogPlayerContentStack[this._activePlayerIndex] });
        this._dialogPlayerContentStack[this._activePlayerIndex] = null;

        // A sub dialog ends; active player becomes one higher up in the stack.
        this._activePlayerIndex--;

        this._dialogPlayers[this._activePlayerIndex].next();
    }

    getPlayMode() {
        return this._dialogPlayers[0].getPlayMode();
    }

    setPlayMode(value) {
        this._dialogPlayers[0].setPlayMode(value);
    }

    // TODO: transfer scores between dialogs so that it doesn't matter that the dialog is hierarchical.
    //       Then again, scores may be obsolete and removed from the dialog player in the near future altogether.
    getScores() {
        return this._dialogPlayers[this._activePlayerIndex].getScores();
    }

    getScore(variableName) {
        return this._dialogPlayers[this._activePlayerIndex].getScore(variableName);
    }

    clearScores() {
        this._dialogPlayers[this._activePlayerIndex].clearScores();
    }

    clearScore(variableName) {
        this._dialogPlayers[this._activePlayerIndex].clearScore(variableName);
    }

    /**
     * Max time only works in the topmost dialog. 
     * Max time in sub dialog makes no sense as the whole sub dialog structure is invisible 
     * for the outside world
     */
    getMaxTime() {
        return this._dialogPlayers[0].getMaxTime();
    }

    getCurrentCommandSequence() {
        return this._dialogPlayers[this._activePlayerIndex].getCurrentCommandSequence();
    }
}
